import React from 'react';
import {Icons} from '../../assets';
import {Community} from '../../models/community';
import {CreateOpportunityFormData, OpportunityVisibility, UploadStatus} from '../../models/opportunity';
import {CommunityBadgesRow} from '../community-badges-row';
import {ExpandableText} from '../expandable-text';
import {Modal, ModalSize} from '../modals/modal';
import {VideoUpload} from '../video-upload';

interface JobPreviewModalProps {
  existingVideoUrl?: string | null;
  formData: CreateOpportunityFormData;
  isOpen: boolean;
  isPublishing?: boolean;
  isSavingDraft?: boolean;
  isUploading?: boolean;
  onBackToEdit: () => void;
  onClose: () => void;
  onPublish: () => void;
  onRemoveVideo: () => void;
  onSaveAsDraft: () => void;
  onVideoUpload: (file: File) => void;
  restrictedToCommunities?: Community[];
  uploadProgress?: number;
  uploadStatus?: UploadStatus;
  videoFile?: File | null;
}

export const JobPreviewModal: React.FC<JobPreviewModalProps> = ({
  existingVideoUrl,
  formData,
  isOpen,
  isPublishing = false,
  isSavingDraft = false,
  isUploading = false,
  onBackToEdit,
  onClose,
  onPublish,
  onRemoveVideo,
  onSaveAsDraft,
  onVideoUpload,
  restrictedToCommunities = [],
  uploadProgress = 0,
  uploadStatus = UploadStatus.UPLOADING,
  videoFile,
}) => {
  const formatSalary = (salary: string) => {
    if (!salary) return '';
    return salary.includes('$') ? salary : `$${salary}`;
  };

  const formatTags = (value: string, type: 'employment' | 'experience' | 'location') => {
    switch (type) {
      case 'employment':
        return value.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
      case 'experience':
        return value.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
      default:
        return value;
    }
  };

  const getSaveAsDraftButtonText = () => {
    if (isUploading) return 'Please wait...';
    if (isSavingDraft) return 'Saving Draft...';
    return 'Save as Draft';
  };

  const getPublishButtonText = () => {
    if (isUploading) return 'Please wait...';
    if (isPublishing) return 'Publishing...';
    return 'Publish';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.XL} className="!max-w-7xl w-[95vw]">
      <div className="bg-white rounded-2xl max-h-[90vh] flex flex-col">
        {/* Sticky Header */}
        <div className="flex items-center justify-center p-6 border-b border-gray-200 bg-white rounded-t-2xl sticky top-0 z-10">
          <h2 className="text-xl font-semibold text-gray-900">Job Preview</h2>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
            {/* Left Column - Job Details */}
            <div className="lg:col-span-3">
              <h1 className="text-3xl font-bold text-gray-900 mb-3">{formData.jobTitle || 'Job Title'}</h1>

              <p className="text-xl text-gray-700 mb-6">{formatSalary(formData.salary)}</p>

              {/* Visibility & Tags */}
              <div className="flex flex-wrap items-center gap-3 mb-8">
                {formData.visibility === OpportunityVisibility.PRIVATE && restrictedToCommunities.length > 0 ? (
                  <CommunityBadgesRow communities={restrictedToCommunities} badgeSize={28} maxVisible={3} />
                ) : formData.visibility === OpportunityVisibility.PUBLIC ? (
                  <img src={Icons.VISIBILITY_PUBLIC} alt="Public" className="shrink-0 w-7 h-7" title="Public" />
                ) : (
                  <span className="text-sm font-medium px-3 py-1.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200">
                    Private (Community-only)
                  </span>
                )}
                {formData.employmentType && (
                  <span className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-full">
                    {formatTags(formData.employmentType, 'employment')}
                  </span>
                )}
                {formData.experienceLevel && (
                  <span className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-full">
                    {formatTags(formData.experienceLevel, 'experience')}
                  </span>
                )}
                {formData.location && <span className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-full">{formData.location}</span>}
              </div>

              {/* Job Description */}
              {formData.jobDescription && (
                <div className="mb-8 break-words">
                  <h3 className="text-lg font-semibold text-gray-900">Job Description</h3>
                  <div className="relative max-h-[20rem] overflow-y-auto rounded-lg">
                    <ExpandableText className="text-gray-700 leading-relaxed whitespace-pre-wrap break-words" maxLength={300}>
                      {formData.jobDescription}
                    </ExpandableText>
                  </div>
                </div>
              )}

              {/* Required Skills */}
              {formData.requiredSkills && formData.requiredSkills.length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Required Skills</h3>
                  <div className="flex flex-wrap gap-3">
                    {formData.requiredSkills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 text-sm rounded-full"
                        style={{
                          backgroundColor: '#EAEFFA',
                          color: '#2E7CF6',
                        }}>
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="lg:col-span-2">
              <div className="sticky top-8">
                <VideoUpload
                  videoFile={videoFile}
                  existingVideoUrl={existingVideoUrl}
                  onVideoUpload={onVideoUpload}
                  onRemoveVideo={onRemoveVideo}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="flex items-center justify-between p-8 border-t border-gray-200 bg-white rounded-b-2xl sticky bottom-0 z-10">
          <button onClick={onBackToEdit} className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">Back to Edit</span>
          </button>

          {isUploading && (
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm text-gray-600">
                  {uploadStatus === UploadStatus.UPLOADING && `Uploading video...`}
                  {uploadStatus === UploadStatus.PROCESSING && `Processing on server...`}
                  {uploadStatus === UploadStatus.COMPLETE && `Video ready!`}
                </span>
                <span className="text-sm text-gray-600">{uploadStatus === UploadStatus.PROCESSING ? '100%' : `${uploadProgress}%`}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-300 ease-out ${
                    uploadStatus === UploadStatus.PROCESSING ? 'bg-orange-500' : 'bg-blue-600'
                  }`}
                  style={{
                    width: uploadStatus === UploadStatus.PROCESSING ? '100%' : `${uploadProgress}%`,
                    animation: uploadStatus === UploadStatus.PROCESSING ? 'pulse 2s infinite' : 'none',
                  }}
                />
              </div>
            </div>
          )}

          <div className="flex space-x-3">
            <button
              onClick={onSaveAsDraft}
              disabled={isSavingDraft || isPublishing || isUploading}
              className="px-6 py-2.5 bg-gray-100 hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium rounded-full transition-colors text-sm">
              {getSaveAsDraftButtonText()}
            </button>

            <button
              onClick={onPublish}
              disabled={isPublishing || isSavingDraft || isUploading}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-medium rounded-full transition-colors text-sm">
              {getPublishButtonText()}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
